import React, { createContext, ReactNode, useContext, useState } from "react";
import { toast } from "react-toastify";

import { ProjectBudget, ProjectBudgetService } from ".";

interface IProps {
	children?: ReactNode;
}

interface IProjectBudgetContextType {
	budget?: ProjectBudget;
	fetchProjectBudget?: (budgetId: number) => any;
	save?: () => void;
	createReportLink?: () => Promise<void>;
	revokeReportLink?: () => Promise<void>;
	setBudget?: React.Dispatch<any>;
}

const ProjectBudgetContext = createContext<IProjectBudgetContextType>({});

export const ProjectBudgetProvider = (props: IProps) => {
	const [budget, setBudget] = useState<ProjectBudget>();

	const projectBudgetService = new ProjectBudgetService();

	const fetchProjectBudget = async (budgetId: number) => {
		const fetched = await projectBudgetService.findById(budgetId);
		setBudget(fetched);

		return budget;
	};

	const save = async () => {
		if (budget) {
			try {
				const updatedBudget = await projectBudgetService.save(budget);
				setBudget(updatedBudget);
			} catch (error) {
				toast.error((error as Error).message);

				// The page edits the budget in place before saving, so reload it
				// to drop the rejected change rather than leave it on screen.
				if (budget.id) {
					setBudget(await projectBudgetService.findById(budget.id));
				}
			}
		}
	};

	const createReportLink = async () => {
		if (budget && budget.id) {
			setBudget(await projectBudgetService.createReportLink(budget.id));
		}
	};

	const revokeReportLink = async () => {
		if (budget && budget.id) {
			setBudget(await projectBudgetService.revokeReportLink(budget.id));
		}
	};

	// pass the value in provider and return
	return (
		<ProjectBudgetContext.Provider
			value={{
				budget,
				fetchProjectBudget,
				save,
				createReportLink,
				revokeReportLink,
				setBudget,
			}}
		>
			{props.children}
		</ProjectBudgetContext.Provider>
	);
};

export const useProjectBudgetContext = () => {
	return useContext(ProjectBudgetContext);
};
